import { useRef, useState } from 'react';
import { useDismissOnOutsideClick } from '../hooks/useDismissOnOutsideClick';
import { ConfirmModal } from './ConfirmModal';
import { useAddTagSuggestions, useTagResources, useTagCollections, useTagSearch } from '../hooks/useTags';
import { useResourceSelectionStore } from '../stores/resourceSelectionStore';
import { useResourceViewerStore } from '../stores/resourceViewerStore';
import { useTagRelated } from '../stores/tagRelatedStore';
import type { BulkSearch } from '../types/search';

/**
 * Context a page supplies so the grid's bulk actions can build their requests. `filter`
 * is the tag-filter object used for all-matching (mode 'all'); null means the whole
 * collection(s). `canManageTags` gates the controls (MANAGE_TAGS permission).
 *
 * `search` is set only by the search-results page; when present, mode-'all' actions
 * scope to the search rather than the collection(s) — mirroring Angular, where the
 * search branches of addTag/report/batch fire instead of the filter branches.
 */
export interface BulkContext {
  projectId: string;
  projectName?: string;
  collectionIds: string[];
  filter: object | null;
  columnFilters?: object | null;
  search?: BulkSearch;
  canManageTags: boolean;
  canCreateBatches: boolean;
  canManageReports: boolean;
}

/**
 * Bulk Add-Tag control — the React port of Angular's iio-add-tag. A tag typeahead
 * (existing tag or free-text new name) + "Tag Related" toggle + a Tag button badged
 * with the selection count. mode 'some' tags the explicit ids; mode 'all' confirms and
 * tags every matching resource via the filter (exclusions aren't expressible there).
 */
export function AddTagControl({ bulk }: { bulk: BulkContext }) {
  const mode = useResourceSelectionStore((s) => s.mode);
  const selected = useResourceSelectionStore((s) => s.selected);
  const clear = useResourceSelectionStore((s) => s.clear);
  const totalRows = useResourceViewerStore((s) => s.totalRows);
  const count = mode === 'all' ? totalRows : Object.keys(selected).length;

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const typeaheadRef = useRef<HTMLDivElement>(null);
  useDismissOnOutsideClick(typeaheadRef, open, () => setOpen(false));
  const [tagRelated, setTagRelated] = useTagRelated();
  const [confirming, setConfirming] = useState(false);

  const { data: suggestions } = useAddTagSuggestions(bulk.projectId, query);
  const tagResources = useTagResources(bulk.projectId);
  const tagCollections = useTagCollections(bulk.projectId, bulk.collectionIds);
  // Angular's search scope is a single collection (SearchController passes
  // `scope.collection ? [scope.collection.id] : []`), so take the first id.
  const tagSearch = useTagSearch(bulk.projectId, bulk.collectionIds[0]);

  const name = query.trim();
  const pending = tagResources.isPending || tagCollections.isPending || tagSearch.isPending;
  const canApply = count > 0 && name.length > 0 && !pending;

  const reset = () => {
    setQuery('');
    setOpen(false);
    setConfirming(false);
    clear();
  };

  const applySome = () =>
    tagResources.mutate({ name, tagRelated, resources: Object.keys(selected) }, { onSuccess: reset });

  const applyAll = () => {
    if (bulk.search) {
      tagSearch.mutate(
        { name, tagRelated, search: bulk.search, columnFilters: bulk.columnFilters ?? null },
        { onSuccess: reset }
      );
      return;
    }
    tagCollections.mutate(
      { name, tagRelated, filter: bulk.filter, columnFilters: bulk.columnFilters ?? null },
      { onSuccess: reset }
    );
  };

  const onTagClick = () => {
    if (!canApply) return;
    if (mode === 'all') setConfirming(true);
    else applySome();
  };

  const shownSuggestions = query ? suggestions ?? [] : [];

  return (
    <div className="add-tag-control" data-testid="bulk-tag-panel">
      <div className="add-tag-row">
      <div className="add-tag-typeahead" ref={typeaheadRef}>
        <input
          type="text"
          className="form-control"
          placeholder="Tag name…"
          data-testid="bulk-tag-name-input"
          value={query}
          disabled={pending}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(e.target.value.length > 0);
          }}
          onFocus={() => setOpen(query.length > 0)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onTagClick();
            }
          }}
        />
        {open && shownSuggestions.length > 0 && (
          <div className="dropdown-menu add-tag-suggestions" style={{ display: 'block' }}>
            {shownSuggestions.map((t) => (
              <div
                key={t.id}
                className="add-tag-suggestion"
                data-testid="bulk-tag-suggestion-item"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setQuery(t.name);
                  setOpen(false);
                }}
              >
                <i className="fas fa-fw fa-circle" style={{ color: t.color }} aria-hidden="true" /> {t.name}
              </div>
            ))}
          </div>
        )}
      </div>

      <button type="button" className="btn btn-primary" disabled={!canApply} onClick={onTagClick} data-testid="bulk-tag-submit-btn">
        <span className="badge">{count.toLocaleString()}</span> Tag <i className="fas fa-plus-circle" />
      </button>
      </div>

      <div className="checkbox checkbox-primary add-tag-related">
        <input id="add-tag-related" data-testid="bulk-tag-related-checkbox" type="checkbox" checked={tagRelated} onChange={(e) => setTagRelated(e.target.checked)} />
        <label htmlFor="add-tag-related">Tag Related</label>
      </div>

      {confirming && (
        <ConfirmModal
          title="Bulk Tag Confirmation"
          testId="bulk-tag-confirm-modal"
          confirmLabel="Apply Tag"
          saving={pending}
          onConfirm={applyAll}
          onClose={() => setConfirming(false)}
        >
          <p>
            Apply <strong>{name}</strong> to all <strong>{count.toLocaleString()}</strong> matching resource
            {count === 1 ? '' : 's'}?
          </p>
          <p className="text-muted">Tag Related: {tagRelated ? 'Yes' : 'No'}</p>
        </ConfirmModal>
      )}
    </div>
  );
}
